import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useMobileSidebar } from '@/contexts/MobileSidebar/MobileSidebarContext';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import useSidebarPinned from '@/hooks/useSidebarPinned';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import type { RefreshableQueryMeta } from '@/hooks/useGlobalRefresh';
import Fund from '@/models/Fund';
import Balance from '@/models/Balance';
import Sidebar from '@/components/sidebar/Sidebar';
import FundBalanceBar from '@/components/FundDisplay/FundBalanceBar';
import FrozenFundNotice from '@/components/FundDisplay/FrozenFundNotice';
import FundContext from './FundContext';
import SidebarBadgeProvider from '@/contexts/SidebarBadge/SidebarBadgeProvider';
import CoordinatorReplyAlert from '@/components/Inquiries/CoordinatorReplyAlert';
import FeatureFlagsProvider from '@/contexts/FeatureFlags/FeatureFlagsProvider';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import useDashboardPrefetch from '@/pages/Dashboard/useDashboardPrefetch';

const LAST_USED_FUND_ID_KEY = 'lastUsedFundId';

/** כתובת הקרן עצמה ("/130", שמפנה למסך הפתיחה) או "תמונת מצב" שלה. */
const DASHBOARD_ENTRY_PATH = /^\/\d+\/?(dashboard\/?)?$/;

const FundProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuthContext();
  const { fundId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: funds, isLoading } = useGetAuthApi<Fund[]>('funds');
  const [currentFund, setCurrentFund] = useState<Fund | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const { isSidebarDocked } = useDeviceDetection();
  const [isPinned, togglePinned] = useSidebarPinned();
  const { isMobileSidebarOpen, closeMobileSidebar } = useMobileSidebar();

  /*
   * SPR-10303: הנתונים של "תמונת מצב" יוצאים לדרך כבר כאן, במקביל לרשימת הקרנות ולדגלים של
   * הקרן, ולא אחריהם (ר' useDashboardPrefetch). הדגל נקרא מהמפה הכללית של המשתמש: הרכיב הזה
   * יושב מעל FeatureFlagsProvider שלו עצמו, והמפה של הקרן עוד לא חזרה. דגל שנפתח רק לקרן
   * מסוימת נקרא שם כסגור - המסך פשוט נטען כמו קודם, בלי הטעינה המוקדמת.
   */
  const dashboardOnForUser = useFeature('dashboard');
  const numericFundId = Number(fundId);
  useDashboardPrefetch(
    Number.isFinite(numericFundId) ? numericFundId : undefined,
    dashboardOnForUser && DASHBOARD_ENTRY_PATH.test(pathname),
  );

  // Auto-close sidebar on route change (only while it is an overlay drawer)
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = pathname;
      if (!isSidebarDocked && isMobileSidebarOpen) {
        closeMobileSidebar();
      }
    }
  }, [pathname, isSidebarDocked, isMobileSidebarOpen, closeMobileSidebar]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!funds) {
      return;
    }

    if (funds.length === 0) {
      setCurrentFund(null);
      return;
    }

    // 1. Try to get from URL (ignore non-numeric ids like "-Infinity" / "undefined")
    const numericFundId = Number(fundId);
    const paramFund = Number.isFinite(numericFundId)
      ? funds.find((f) => f.id === numericFundId)
      : undefined;
    if (paramFund) {
      setCurrentFund(paramFund);
      localStorage.setItem(LAST_USED_FUND_ID_KEY, String(paramFund.id));
      return;
    }

    // 2. Try to get from localStorage
    const storedFundId = localStorage.getItem(LAST_USED_FUND_ID_KEY);
    if (storedFundId) {
      const storedFund = funds.find((f) => f.id === Number(storedFundId));
      if (storedFund) {
        navigate(`../${storedFund.id}`, { replace: true });
        return;
      }
    }

    // 3. Fallback - fund with highest ID
    const latestFundId = Math.max(...funds.map((f) => f.id));
    if (!Number.isFinite(latestFundId)) {
      setCurrentFund(null);
      return;
    }
    navigate(`../${latestFundId}`, { replace: true });
  }, [fundId, funds, navigate]);

  const { data: balanceData } = useGetAuthApi<Balance>(currentFund ? `funds/financial-data/${currentFund.id}/balance` : '', undefined, {
    /* היתרה שבפס העליון היא חלק מהמסך בכל לוח, ולכן מתרעננת עם כל רענון. */
    meta: { refreshable: true } satisfies RefreshableQueryMeta,
  });

  useEffect(() => {
    if (balanceData) {
      const balanceObj = Object.assign(new Balance(), balanceData);
      setBalance(balanceObj);
    } else {
      setBalance(null);
    }
  }, [balanceData]);

  const handleFundSelectChange = (fundId: number) => {
    localStorage.setItem(LAST_USED_FUND_ID_KEY, String(fundId));
    const segments = pathname.split('/');
    const route = segments.map((s) => (s === `${currentFund?.id}` ? fundId : s)).join('/');
    navigate(route);
  };

  const isExpanded = isSidebarDocked || isMobileSidebarOpen;
  const showOverlay = !isSidebarDocked && isMobileSidebarOpen;
  const isRail = isSidebarDocked && !isPinned;

  return (
    <FundContext.Provider value={{ currentFund, balance }}>
      <FeatureFlagsProvider>
      <SidebarBadgeProvider>
      <div className={`sidebar-container ${isExpanded ? 'sidebar-open' : ''} ${isRail ? 'sidebar-rail' : ''}`}>
          <Sidebar isExpanded={isExpanded} isPinned={isPinned} isRail={isRail} onTogglePinned={togglePinned} />
      </div>
      {showOverlay && (
        <div className="sidebar-overlay" onClick={closeMobileSidebar} aria-hidden="true" />
      )}
      {/* מצלצל לרכז כשהמשרד עונה. כאן ולא בשורש האפליקציה, כי הנתיב הוא לפי קרן. */}
      <CoordinatorReplyAlert />
      <div className="content-container">
        {/* הקרן והיתרה בראש כל מסך, בלי תלות במצב התפריט */}
        <FundBalanceBar funds={funds ?? []} isLoading={isLoading} onSelectFund={handleFundSelectChange} />
        {/* HAT-1301: why a frozen fund accepts nothing, and how it is reactivated */}
        <FrozenFundNotice />
        {children}
      </div>
      </SidebarBadgeProvider>
      </FeatureFlagsProvider>
    </FundContext.Provider>
  );
};

export default FundProvider;